/**
 * GlobalVoiceAssistant.jsx
 * Compact, Low-Latency Production Mobile Voice Assistant & Controller for Kisan Mithr.
 * 
 * Design Characteristics:
 * - Ultra-small floating circular voice orb (bottom-5 right-5)
 * - Real state machine: IDLE -> ACTIVATING -> LISTENING -> UNDERSTANDING -> EXECUTING -> SPEAKING/RESPONDING -> ERROR -> IDLE
 * - Live partial transcripts streamed word-by-word from Sarvam Realtime STT
 * - Real-time audio reactive volume pulse & dynamic sound wave visualizer
 * - Barge-in awareness: user speaking or tapping instantly halts TTS playback
 * - Minimalist floating popup card (~310px wide) displaying live transcript and concise spoken feedback
 * - Deep agricultural handoff banner for /assistant recommendations
 * - Full accessibility (aria-live, keyboard focus, prefers-reduced-motion)
 */

import React, { useEffect, useRef, useState } from "react";
import { useVoice, VOICE_STATES } from "../VoiceContext";
import {
  Mic,
  X,
  ArrowRight,
  Check,
  Loader2,
  Volume2,
  ShieldAlert,
  Keyboard
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export const GlobalVoiceAssistant = () => {
  const {
    voiceState,
    transcript,
    partialTranscript,
    assistantReply,
    volume,
    errorMessage,
    startListeningCommand,
    stopListening,
    cancelVoice,
    autoWakeWord,
    isWidgetExpanded,
    setIsWidgetExpanded,
    executeTextCommand
  } = useVoice();

  const navigate = useNavigate();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textCommand, setTextCommand] = useState("");
  const hideTimerRef = useRef(null);
  const inputRef = useRef(null);

  const isActivating = voiceState === VOICE_STATES.ACTIVATING;
  const isListening = voiceState === VOICE_STATES.LISTENING;
  const isTranscribing = voiceState === VOICE_STATES.TRANSCRIBING;
  const isUnderstanding = voiceState === VOICE_STATES.UNDERSTANDING;
  const isExecuting = voiceState === VOICE_STATES.EXECUTING;
  const isResponding = voiceState === VOICE_STATES.RESPONDING;
  const isSpeaking = voiceState === VOICE_STATES.SPEAKING;
  const isError = voiceState === VOICE_STATES.ERROR;
  const isIdle = voiceState === VOICE_STATES.IDLE;

  // Show popup whenever active voice interaction begins or widget is expanded
  useEffect(() => {
    if (isActivating || isListening || isTranscribing || isUnderstanding || isExecuting || isResponding || isSpeaking || isError || isWidgetExpanded) {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      setIsPopupVisible(true);
    } else if (isIdle && !showTextInput) {
      // Auto-collapse popup after 3.8s of idle so website stays completely unobstructed
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setIsPopupVisible(false);
        if (setIsWidgetExpanded) setIsWidgetExpanded(false);
      }, 3800);
    }

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [voiceState, isActivating, isListening, isTranscribing, isUnderstanding, isExecuting, isResponding, isSpeaking, isError, isIdle, isWidgetExpanded, showTextInput, setIsWidgetExpanded]);

  // Focus input when text input is opened
  useEffect(() => {
    if (showTextInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showTextInput]);

  const handleClosePopup = () => {
    cancelVoice();
    setIsPopupVisible(false);
    setShowTextInput(false);
    if (setIsWidgetExpanded) setIsWidgetExpanded(false);
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (!textCommand || !textCommand.trim()) return;
    if (executeTextCommand) {
      executeTextCommand(textCommand.trim());
    }
    setTextCommand("");
    setShowTextInput(false);
  };

  // Handle floating orb tap (with instant barge-in interruption)
  const handleOrbClick = () => {
    if (isListening || isActivating) {
      stopListening();
    } else if (isSpeaking || isResponding) {
      // User tapped orb while speaking -> barge-in instant interruption & restart listening!
      cancelVoice();
      setTimeout(() => {
        startListeningCommand();
      }, 100);
    } else {
      startListeningCommand();
    }
  };

  // Status Badge Label & Colors
  const getStatusInfo = () => {
    if (isActivating) {
      return {
        label: "Starting...",
        badgeClass: "bg-amber-500/20 text-amber-300 border-amber-500/40 animate-pulse",
        dotClass: "bg-amber-400 animate-ping"
      };
    }
    if (isListening) {
      return {
        label: "Listening...",
        badgeClass: "bg-red-500/20 text-red-300 border-red-500/40 animate-pulse",
        dotClass: "bg-red-500 animate-ping"
      };
    }
    if (isTranscribing) {
      return {
        label: "Transcribing...",
        badgeClass: "bg-blue-500/20 text-blue-300 border-blue-500/40 animate-pulse",
        dotClass: "bg-blue-400 animate-pulse"
      };
    }
    if (isUnderstanding) {
      return {
        label: "Understanding...",
        badgeClass: "bg-purple-500/20 text-purple-300 border-purple-500/40 animate-pulse",
        dotClass: "bg-purple-400"
      };
    }
    if (isExecuting) {
      return {
        label: "Executing...",
        badgeClass: "bg-amber-500/20 text-amber-300 border-amber-500/40 animate-pulse",
        dotClass: "bg-amber-400 animate-bounce"
      };
    }
    if (isSpeaking) {
      return {
        label: "Speaking...",
        badgeClass: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40 shadow-[0_0_12px_rgba(6,182,212,0.25)]",
        dotClass: "bg-cyan-400 animate-pulse"
      };
    }
    if (isResponding) {
      return {
        label: "Done",
        badgeClass: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
        dotClass: "bg-emerald-400"
      };
    }
    if (isError) {
      return {
        label: "Error",
        badgeClass: "bg-red-500/20 text-red-300 border-red-500/50",
        dotClass: "bg-red-500"
      };
    }
    return {
      label: "Hey Kisan",
      badgeClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      dotClass: "bg-emerald-400 animate-pulse"
    };
  };

  const status = getStatusInfo();

  // Dynamic Audio-Reactive Scale on Orb during LISTENING
  const dynamicScale = isListening
    ? Math.min(1.0 + Math.max(0, volume || 0) * 0.45, 1.35)
    : 1.0;

  // Check if assistant reply is a local voice assistant handoff
  const isHandoff = assistantReply && assistantReply.toLowerCase().includes("voice assistant feature");

  return (
    <aside
      aria-label="Kisan Mithr Global Voice Assistant"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[80] flex flex-col items-end pointer-events-none select-none font-sans max-w-[calc(100vw-20px)]"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)"
      }}
    >
      {/* ─────────────────────────────────────────────────────────────
          COMPACT FLOATING POPUP (Fluid on Mobile, Max-width 330px)
         ───────────────────────────────────────────────────────────── */}
      {isPopupVisible && (
        <div
          role="region"
          aria-live="polite"
          className="pointer-events-auto mb-3 w-[calc(100vw-32px)] max-w-[330px] rounded-2xl bg-zinc-950/95 backdrop-blur-2xl border border-white/10 p-3.5 shadow-[0_20px_60px_rgba(0,0,0,0.85)] ring-1 ring-emerald-500/20 transition-all duration-200 transform origin-bottom-right animate-in fade-in slide-in-from-bottom-2"
        >
          {/* Header Line */}
          <div className="flex items-center justify-between gap-2 border-b border-white/5 pb-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${status.dotClass}`} />
                <span className={`relative inline-flex rounded-full h-2 w-2 ${status.dotClass}`} />
              </span>
              <span className="text-xs font-bold tracking-wide text-zinc-200 flex items-center gap-1.5">
                Kisan Mithr
                <span className="text-[10px] text-zinc-500 font-normal">Controller</span>
              </span>

              {/* Live Audio Visualizer bars during Listening */}
              {isListening && (
                <div className="flex items-center gap-0.5 h-3 ml-1">
                  {[0.4, 0.9, 0.6, 0.8].map((factor, idx) => {
                    const height = Math.max(3, Math.min(12, 3 + (volume || 0) * 14 * factor));
                    return (
                      <span
                        key={idx}
                        className="w-0.5 bg-red-400 rounded-full transition-all duration-75"
                        style={{ height: `${height}px` }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${status.badgeClass}`}>
                {status.label}
              </span>
              <button
                onClick={() => setShowTextInput((prev) => !prev)}
                className={`p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center rounded-full transition-colors touch-manipulation ${
                  showTextInput
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                    : "text-zinc-400 hover:text-white hover:bg-white/10"
                }`}
                aria-label="Toggle text command input"
                title={showTextInput ? "Close text mode" : "Type a command"}
              >
                <Keyboard size={14} />
              </button>
              <button
                onClick={handleClosePopup}
                className="p-1.5 min-w-[32px] min-h-[32px] flex items-center justify-center rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors touch-manipulation"
                aria-label="Close voice assistant popup"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Transcript / Action Body */}
          <div className="space-y-1.5 min-h-[38px] flex flex-col justify-center">
            {/* 1. Live Interim / Partial Transcript while speaking */}
            {isListening && partialTranscript ? (
              <p className="text-xs text-zinc-200 font-medium leading-snug break-words">
                <span className="text-emerald-300">"{partialTranscript}</span>
                <span className="inline-block w-1.5 h-3 ml-0.5 bg-emerald-400 rounded-sm animate-pulse align-middle" />
                <span className="text-emerald-300">"</span>
              </p>
            ) : isListening && !transcript ? (
              <p className="text-xs text-zinc-400 animate-pulse flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-ping" />
                Listening... Speak your command
              </p>
            ) : isActivating ? (
              <p className="text-xs text-amber-300 animate-pulse flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin text-amber-400" />
                Connecting to microphone...
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {transcript && (
                  <p className="text-xs text-zinc-300 font-medium line-clamp-2 italic leading-snug">
                    "{transcript}"
                  </p>
                )}
                {isTranscribing && (
                  <p className="text-xs text-zinc-400 animate-pulse flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin text-blue-400" />
                    Transcribing...
                  </p>
                )}
                {isUnderstanding && (
                  <p className="text-xs text-zinc-400 animate-pulse flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin text-purple-400" />
                    Understanding command...
                  </p>
                )}
                {isExecuting && (
                  <p className="text-xs text-zinc-400 flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin text-amber-400" />
                    Executing website action...
                  </p>
                )}
                {!transcript && !isTranscribing && !isUnderstanding && !isExecuting && (
                  <p className="text-xs text-zinc-500">
                    Say "Go to weather", "Open menu", or "Switch to Telugu"
                  </p>
                )}
              </div>
            )}

            {/* Assistant Spoken / Written Feedback */}
            {assistantReply && (
              <div className="text-xs text-emerald-400 font-semibold flex items-start gap-1.5 pt-0.5">
                {isSpeaking ? (
                  <Volume2 size={13} className="flex-shrink-0 mt-0.5 text-cyan-400 animate-pulse" />
                ) : (
                  <Check size={13} className="flex-shrink-0 mt-0.5 text-emerald-400" />
                )}
                <span className="line-clamp-2 leading-tight">
                  {assistantReply}
                </span>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && !assistantReply && (
              <div className="text-xs text-red-400 flex items-center gap-1.5">
                <ShieldAlert size={13} className="text-red-400 flex-shrink-0" />
                <span className="line-clamp-1">{errorMessage}</span>
              </div>
            )}

            {/* Quick Action Button during Active Listening */}
            {isListening && (
              <div className="pt-2 border-t border-white/10 mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => stopListening()}
                  className="flex-1 py-1.5 px-3 bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 border border-red-500/40 text-red-200 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all touch-manipulation active:scale-[0.98]"
                >
                  <span className="w-2 h-2 rounded-full bg-red-400 animate-ping" />
                  <span>Tap when done speaking</span>
                </button>
                <button
                  type="button"
                  onClick={handleClosePopup}
                  className="py-1.5 px-3 bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-600 text-zinc-300 hover:text-white rounded-lg text-xs font-medium transition-all touch-manipulation"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Quick Stop Button during Speaking */}
            {isSpeaking && (
              <div className="pt-2 border-t border-white/10 mt-1 flex items-center justify-between">
                <span className="text-xs text-cyan-300 flex items-center gap-1.5">
                  <Volume2 size={13} className="animate-pulse" />
                  Speaking...
                </span>
                <button
                  type="button"
                  onClick={() => cancelVoice()}
                  className="py-1 px-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-zinc-200 rounded-lg text-xs font-medium transition-all touch-manipulation"
                >
                  Stop
                </button>
              </div>
            )}

            {/* Local Assistant Handoff Action Button */}
            {isHandoff && (
              <div className="pt-2 border-t border-white/5 mt-2">
                <button
                  onClick={() => {
                    navigate("/assistant");
                    setIsPopupVisible(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-1.5 px-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-semibold shadow-md transition-all touch-manipulation"
                >
                  <span>Open Voice Assistant</span>
                  <ArrowRight size={13} />
                </button>
              </div>
            )}

            {/* Subtle Text Input Mode for typing */}
            {showTextInput && (
              <form onSubmit={handleTextSubmit} className="mt-2 pt-2 border-t border-white/10 flex items-center gap-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={textCommand}
                  onChange={(e) => setTextCommand(e.target.value)}
                  placeholder="Type command (e.g. go to weather)..."
                  className="flex-1 bg-zinc-900/90 border border-white/15 focus:border-emerald-500 rounded-lg px-2.5 py-1 text-xs text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={!textCommand.trim()}
                  className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors touch-manipulation"
                >
                  Send
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          FLOATING CIRCULAR ORB BUTTON (Compact ~52px)
         ───────────────────────────────────────────────────────────── */}
      <div className="relative pointer-events-auto">
        {/* Subtle Outer Ambient Glow Ring */}
        <div
          className={`absolute -inset-1.5 rounded-full blur-md pointer-events-none transition-all duration-300 ${
            isActivating
              ? "bg-amber-500/35 animate-pulse"
              : isListening
              ? "bg-red-500/40 animate-pulse"
              : isTranscribing
              ? "bg-blue-500/35 animate-pulse"
              : isUnderstanding
              ? "bg-purple-500/35 animate-spin"
              : isExecuting
              ? "bg-amber-500/35 animate-pulse"
              : isSpeaking
              ? "bg-cyan-500/45 animate-pulse"
              : isResponding
              ? "bg-emerald-500/40"
              : isError
              ? "bg-red-600/30"
              : "bg-emerald-500/20 hover:bg-emerald-500/35"
          }`}
          style={{
            transform: `scale(${dynamicScale})`
          }}
        />

        {/* Circular Button */}
        <button
          onClick={handleOrbClick}
          aria-label={
            isListening
              ? "Stop listening to Kisan Mithr voice assistant"
              : isSpeaking
              ? "Interrupt assistant speech"
              : "Activate Kisan Mithr Global Voice Assistant"
          }
          className={`relative w-[52px] h-[52px] sm:w-[56px] sm:h-[56px] rounded-full flex items-center justify-center border backdrop-blur-xl shadow-2xl transition-all duration-200 outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-black touch-manipulation active:scale-95 ${
            isActivating
              ? "bg-amber-950/80 border-amber-500 text-amber-300 shadow-[0_0_20px_rgba(245,158,11,0.5)]"
              : isListening
              ? "bg-red-950/80 border-red-500 text-red-300 shadow-[0_0_20px_rgba(239,68,68,0.5)]"
              : isTranscribing
              ? "bg-blue-950/80 border-blue-500 text-blue-300 shadow-[0_0_20px_rgba(59,130,246,0.4)]"
              : isUnderstanding
              ? "bg-purple-950/80 border-purple-500 text-purple-300 shadow-[0_0_20px_rgba(168,85,247,0.4)]"
              : isExecuting
              ? "bg-amber-950/80 border-amber-500 text-amber-300 shadow-[0_0_20px_rgba(245,158,11,0.4)]"
              : isSpeaking
              ? "bg-cyan-950/90 border-cyan-400 text-cyan-300 shadow-[0_0_20px_rgba(6,182,212,0.5)]"
              : isResponding
              ? "bg-emerald-950/80 border-emerald-500 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.4)]"
              : isError
              ? "bg-red-950 border-red-600 text-red-400"
              : "bg-zinc-950/90 border-emerald-500/40 text-emerald-400 hover:border-emerald-400 hover:text-emerald-300 hover:scale-105"
          }`}
          style={{
            transform: isListening ? `scale(${dynamicScale})` : undefined
          }}
        >
          {isActivating ? (
            <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
          ) : isListening ? (
            <Mic className="w-5 h-5 animate-pulse text-red-400" />
          ) : isTranscribing ? (
            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
          ) : isUnderstanding ? (
            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
          ) : isExecuting ? (
            <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
          ) : isSpeaking ? (
            <Volume2 className="w-5 h-5 text-cyan-400 animate-pulse" />
          ) : isResponding ? (
            <Check className="w-5 h-5 text-emerald-400" />
          ) : (
            <div className="relative flex items-center justify-center">
              <Mic className="w-5 h-5 text-emerald-400" />
              {autoWakeWord && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-zinc-950" />
              )}
            </div>
          )}
        </button>
      </div>
    </aside>
  );
};

export default GlobalVoiceAssistant;
